/**
 * Mapping of form template IDs to their hospital field paths in Part 1 data.
 * Forms may have multiple hospital fields (e.g., CTO6 has hospitalDetained, hospitalTransferred, responsibleHospital).
 * Forms without hospital fields (like A3, A4, etc.) are not included - they return empty array.
 */
const FORM_TEMPLATE_HOSPITAL_FIELDS = {
    // Admission forms with hospital
    "mha-a1": ["hospital"],
    "mha-a2": ["hospital"],
    "mha-a5": ["hospital"],
    "mha-a6": ["hospital"],
    "mha-a9": ["hospital"],
    "mha-a10": ["hospital"],
    // Admission forms WITHOUT hospital in Part 1 (doctor recommendation forms)
    // "mha-a3": [], // No hospital - medical recommendation
    // "mha-a4": [], // No hospital - joint medical recommendations
    // "mha-a7": [], // No hospital - medical recommendation for emergency
    // "mha-a8": [], // No hospital - medical recommendation for emergency (joint)
    // "mha-a11": [], // No hospital - medical recommendation for guardianship
    // Hospital forms
    "mha-h1": ["hospital"],
    "mha-h2": ["hospital"],
    "mha-h3": ["hospital"],
    "mha-h4": ["hospital", "hospitalDetained"],
    "mha-h5": ["hospital"],
    "mha-h6": ["hospital"],
    // CTO forms
    "mha-cto3": ["hospital"],
    "mha-cto4": ["hospital"],
    "mha-cto5": ["hospital"],
    "mha-cto6": [
        "hospitalDetained",
        "hospitalTransferred",
        "responsibleHospital",
    ],
    "mha-cto7": ["hospital"],
    "mha-cto8": ["hospital"],
    "mha-cto10": ["hospital", "receivingHospital"],
    // CTO forms WITHOUT hospital in Part 1
    // "mha-cto1": [], // No hospital
    // "mha-cto2": [], // No hospital
    // "mha-cto9": [], // No hospital
    // "mha-cto12": [], // No hospital
    // Discharge forms
    "mha-s23": ["hospital"],
    "mha-hpd": ["hospital"],
    // Miscellaneous forms
    "mha-m1": ["admissionHospital", "responsibleHospital"],
    "mha-m2": ["hospital"],
    // Treatment forms WITHOUT hospital
    // "mha-t2": [], // No hospital
    // "mha-t4": [], // No hospital
};
/**
 * Extracts all hospital IDs from Part 1 form data based on the form template.
 *
 * @param formTemplateId - The form template ID (e.g., "mha-a2", "mha-cto6")
 * @param part1Data - The Part 1 form data object
 * @returns Array of hospital IDs found in the form data (empty array if form has no hospital fields or no hospitals selected)
 */
export function getHospitalIdsFromPart1Data(formTemplateId, part1Data) {
    const hospitalFields = FORM_TEMPLATE_HOSPITAL_FIELDS[formTemplateId];
    if (!hospitalFields || hospitalFields.length === 0) {
        return [];
    }
    const hospitalIds = [];
    for (const fieldPath of hospitalFields) {
        const hospitalData = part1Data[fieldPath];
        if (hospitalData?.hospitalId) {
            hospitalIds.push(hospitalData.hospitalId);
        }
    }
    // Remove duplicates (in case same hospital is selected in multiple fields)
    return [...new Set(hospitalIds)];
}
/**
 * Returns the list of hospital field paths for a given form template.
 * Useful for understanding which fields to check on a form.
 *
 * @param formTemplateId - The form template ID
 * @returns Array of field paths, or empty array if form has no hospital fields
 */
export function getHospitalFieldsForTemplate(formTemplateId) {
    return FORM_TEMPLATE_HOSPITAL_FIELDS[formTemplateId] || [];
}
/**
 * Checks if a form template has any hospital fields in Part 1.
 *
 * @param formTemplateId - The form template ID
 * @returns true if the form has hospital fields, false otherwise
 */
export function formTemplateHasHospitalField(formTemplateId) {
    const fields = FORM_TEMPLATE_HOSPITAL_FIELDS[formTemplateId];
    return !!fields && fields.length > 0;
}
//# sourceMappingURL=hospitalFieldExtractor.js.map